import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  CircleDollarSign,
  Clock4,
  FileText,
  FilePlus2,
  Package,
  PiggyBank,
  Receipt as ReceiptIcon,
  ScrollText,
  Settings as SettingsIcon,
  Sparkles,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  Factory,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { useAuth } from "@/auth";
import type { FeatureName } from "@/lib/features";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useTheme } from "@/contexts/ThemeContext";
import { APP_TAGLINE } from "@/lib/brand";
import { formatDate, formatNumber } from "@/lib/format";

type IconType = typeof FileText;

interface RecentReceipt {
  id: number;
  receipt_number: string;
  customer_name: string;
  amount_paid: number;
  payment_method: string;
  payment_date: string | null;
  created_at: string | null;
}

interface RecentInvoice {
  id: number;
  invoice_number: string;
  customer_name: string;
  status: string;
  total_amount: number;
  invoice_date: string | null;
  created_at: string | null;
}

interface SummaryResponse {
  totalRevenue: number;
  outstanding: number;
  receiptsCount: number;
  invoicesCount: number;
  recentReceipts: RecentReceipt[];
  recentInvoices: RecentInvoice[];
}

interface SalesPerformanceMonthly {
  month: string;
  total: number;
}

interface SalesPerformanceProduct {
  product_id: number | null;
  name: string;
  total: number;
}

interface SalesPerformanceUser {
  user_id: number | null;
  name: string;
  total: number;
}

interface SalesPerformanceResponse {
  monthly: SalesPerformanceMonthly[];
  products: SalesPerformanceProduct[];
  salespeople: SalesPerformanceUser[];
}

const CURRENCY = "GH\u20B5";

function greetingFor(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function statusBadge(status: string): string {
  const map: Record<string, string> = {
    draft: "bg-slate-500/15 text-slate-500 dark:text-slate-300 border-slate-500/30",
    sent: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 border-cyan-500/30",
    paid: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30",
    cancelled: "bg-rose-500/15 text-rose-600 dark:text-rose-300 border-rose-500/30",
  };
  return (
    map[status] ??
    "bg-violet-500/15 text-violet-600 dark:text-violet-300 border-violet-500/30"
  );
}

function Shimmer({ className = "" }: { className?: string }) {
  return (
    <div
      className={`h-7 w-24 rounded-md bg-muted animate-pulse ${className}`}
      aria-hidden
    />
  );
}

interface QuickPillProps {
  to: string;
  label: string;
  icon: IconType;
}

function QuickPill({ to, label, icon: Icon }: QuickPillProps) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-3.5 py-1.5 text-sm font-medium text-white shadow-sm backdrop-blur-sm transition hover:-translate-y-0.5 hover:bg-white/25"
    >
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );
}

interface KpiTileProps {
  label: string;
  value: string;
  hint: string;
  icon: IconType;
  tone: string;
  loading: boolean;
}

function KpiTile({ label, value, hint, icon: Icon, tone, loading }: KpiTileProps) {
  return (
    <Card className="shadow-card overflow-hidden transition hover:-translate-y-0.5 hover:shadow-lg">
      <CardContent className="p-5 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground">{label}</div>
          {loading ? (
            <div className="mt-2">
              <Shimmer className="h-8 w-28" />
            </div>
          ) : (
            <div className="text-3xl font-bold mt-1 truncate text-foreground">
              {value}
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1">{hint}</div>
        </div>
        <div
          className={`h-12 w-12 rounded-xl bg-gradient-to-br ${tone} flex items-center justify-center shadow-card shrink-0`}
        >
          <Icon className="h-6 w-6 text-white" />
        </div>
      </CardContent>
    </Card>
  );
}

interface ShortcutDef {
  key: string;
  label: string;
  to: string;
  icon: IconType;
  tone: string;
  module: string;
  description: string;
  // Optional product-level feature flag. When set and OFF, the tile is hidden.
  feature?: FeatureName;
}

const SHORTCUTS: ShortcutDef[] = [
  {
    key: "quotations",
    label: "Quotations",
    to: "/quotations",
    icon: FileText,
    tone: "from-violet-500 to-fuchsia-400",
    module: "quotations",
    description: "Draft and send quotes",
  },
  {
    key: "receipts",
    label: "Receipts",
    to: "/receipts",
    icon: ReceiptIcon,
    tone: "from-emerald-500 to-teal-400",
    module: "receipts",
    description: "Record incoming payments",
  },
  {
    key: "waybills",
    label: "Waybills",
    to: "/waybills",
    icon: Truck,
    tone: "from-blue-500 to-cyan-400",
    module: "waybills",
    description: "Track deliveries",
  },
  {
    key: "sales_invoices",
    label: "Sales Invoices",
    to: "/sales-invoices",
    icon: ScrollText,
    tone: "from-amber-500 to-orange-400",
    module: "sales_invoices",
    description: "Bill your customers",
  },
  {
    key: "production_orders",
    label: "Production Orders",
    to: "/production-orders",
    icon: Factory,
    tone: "from-indigo-500 to-violet-400",
    module: "production_orders",
    description: "Factory production workflow",
    feature: "production_orders",
  },
  {
    key: "customers",
    label: "Customers",
    to: "/customers",
    icon: Users,
    tone: "from-pink-500 to-rose-400",
    module: "customers",
    description: "Manage your accounts",
  },
  {
    key: "products",
    label: "Inventory",
    to: "/products",
    icon: Package,
    tone: "from-indigo-500 to-violet-400",
    module: "products",
    description: "Catalog and pricing",
  },
  {
    key: "finance",
    label: "Finance",
    to: "/finance",
    icon: PiggyBank,
    tone: "from-green-500 to-emerald-400",
    module: "finance",
    description: "Cash-in and outstanding",
  },
  {
    key: "settings",
    label: "Settings",
    to: "/settings",
    icon: SettingsIcon,
    tone: "from-slate-500 to-zinc-400",
    module: "settings",
    description: "Company and access",
  },
];

const NEW_ACTIONS: Array<{
  to: string;
  label: string;
  icon: IconType;
  permission: string;
}> = [
  { to: "/quotations/new", label: "New Quotation", icon: FilePlus2, permission: "quotes.write" },
  { to: "/receipts/new", label: "New Receipt", icon: ReceiptIcon, permission: "receipts.write" },
  { to: "/waybills/new", label: "New Waybill", icon: Truck, permission: "waybills.write" },
  { to: "/sales-invoices/new", label: "New Sales Invoice", icon: ScrollText, permission: "sales_invoices.create" },
];

export default function Dashboard() {
  const { user, can, canAccess, isFeatureEnabled } = useAuth();
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [performance, setPerformance] = useState<SalesPerformanceResponse | null>(null);
  const [performanceLoading, setPerformanceLoading] = useState<boolean>(true);
  const [performanceError, setPerformanceError] = useState<string | null>(null);

  const hasFinance = canAccess("finance");

  useEffect(() => {
    if (!hasFinance) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<SummaryResponse>("/finance/summary");
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (cancelled) return;
        // 403s are expected when finance access is partial; stay silent in that
        // case so the dashboard still feels welcoming.
        if (!(err instanceof ApiError) || err.status !== 403) {
          toast.error((err as Error).message || "Failed to load dashboard");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasFinance]);

  useEffect(() => {
    if (!hasFinance) {
      setPerformanceLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<SalesPerformanceResponse>(
          "/finance/sales-performance",
        );
        if (!cancelled) {
          setPerformance(data);
          setPerformanceError(null);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) return;
        setPerformanceError("Couldn't load sales performance");
      } finally {
        if (!cancelled) setPerformanceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasFinance]);

  const now = useMemo(() => new Date(), []);
  const greeting = greetingFor(now.getHours());
  const displayName =
    user?.name?.split(" ")[0] || user?.username || user?.email || "there";

  const kpis = useMemo(() => {
    const revenue = summary?.totalRevenue ?? 0;
    const receiptsThisMonth = summary?.receiptsCount ?? 0;
    const invoicesThisMonth = summary?.invoicesCount ?? 0;
    const avgInvoice =
      summary && summary.invoicesCount > 0
        ? summary.totalRevenue / summary.invoicesCount
        : 0;
    return [
      {
        label: "Total Revenue",
        value: `${CURRENCY} ${formatNumber(revenue)}`,
        hint: "Cash-in across all receipts",
        icon: Wallet,
        tone: "from-emerald-500 to-teal-400",
      },
      {
        label: "Outstanding",
        value: `${CURRENCY} ${formatNumber(summary?.outstanding ?? 0)}`,
        hint: "Invoiced and awaiting payment",
        icon: Clock4,
        tone: "from-amber-500 to-orange-400",
      },
      {
        label: "Receipts (this month)",
        value: String(receiptsThisMonth),
        hint:
          avgInvoice > 0
            ? `Avg invoice ${CURRENCY} ${formatNumber(avgInvoice)}`
            : "Count of receipts created",
        icon: TrendingUp,
        tone: "from-violet-500 to-fuchsia-400",
      },
      {
        label: "Invoices (this month)",
        value: String(invoicesThisMonth),
        hint: "Sales invoices issued",
        icon: CircleDollarSign,
        tone: "from-blue-500 to-cyan-400",
      },
    ];
  }, [summary]);

  const visibleNewActions = NEW_ACTIONS.filter((a) => can(a.permission));
  const visibleShortcuts = SHORTCUTS.filter(
    (s) =>
      canAccess(s.module) && (!s.feature || isFeatureEnabled(s.feature)),
  );

  const todayLabel = now.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const monthlyTotal =
    (summary?.receiptsCount ?? 0) + (summary?.invoicesCount ?? 0);

  return (
    <div className="space-y-6 max-w-[1400px] animate-fade-in">
      <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-primary text-primary-foreground shadow-card">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-12 bottom-[-60px] h-48 w-48 rounded-full bg-fuchsia-300/20 blur-3xl"
        />
        <div className="relative p-6 md:p-8 flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium uppercase tracking-wide text-white/90 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5" />
                {APP_TAGLINE}
              </div>
              <h1 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight">
                {greeting}, {displayName}
              </h1>
              <p className="mt-1.5 text-sm md:text-base text-white/85">
                {todayLabel}
                {hasFinance && summary
                  ? ` · ${monthlyTotal} document${monthlyTotal === 1 ? "" : "s"} touched this month`
                  : " · Let's get something done today"}
              </p>
            </div>
          </div>

          {visibleNewActions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-white/70 mr-1">
                What would you like to do?
              </span>
              {visibleNewActions.map((a) => (
                <QuickPill
                  key={a.to}
                  to={a.to}
                  label={a.label}
                  icon={a.icon}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {hasFinance && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <KpiTile
              key={k.label}
              label={k.label}
              value={k.value}
              hint={k.hint}
              icon={k.icon}
              tone={k.tone}
              loading={loading}
            />
          ))}
        </section>
      )}

      {hasFinance && (
        <SalesPerformanceCard
          loading={performanceLoading}
          data={performance}
          error={performanceError}
        />
      )}

      {hasFinance && (
        <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Card className="shadow-card xl:col-span-2 overflow-hidden">
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
              <div>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <CardDescription>
                  Latest receipts and invoices across the business.
                </CardDescription>
              </div>
              <Link
                to="/finance"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                Open Finance
                <ArrowRight className="h-4 w-4" />
              </Link>
            </CardHeader>
            <CardContent className="pt-0">
              <Tabs defaultValue="receipts" className="w-full">
                <TabsList className="grid grid-cols-2 w-full max-w-xs">
                  <TabsTrigger value="receipts">Receipts</TabsTrigger>
                  <TabsTrigger value="invoices">Invoices</TabsTrigger>
                </TabsList>

                <TabsContent value="receipts" className="mt-4">
                  <ActivityList
                    loading={loading}
                    emptyMessage="No receipts yet — record your first payment to see it here."
                    rows={(summary?.recentReceipts ?? []).slice(0, 6).map((r) => ({
                      id: `r-${r.id}`,
                      title: r.receipt_number || "—",
                      subtitle: r.customer_name || "Walk-in customer",
                      meta: r.payment_date
                        ? formatDate(r.payment_date)
                        : r.created_at
                          ? formatDate(r.created_at)
                          : "—",
                      amount: `${CURRENCY} ${formatNumber(r.amount_paid)}`,
                      tone: "emerald",
                      badgeLabel: r.payment_method || "Cash",
                      badgeClass: statusBadge("paid"),
                    }))}
                  />
                </TabsContent>

                <TabsContent value="invoices" className="mt-4">
                  <ActivityList
                    loading={loading}
                    emptyMessage="No invoices yet — issue your first invoice from a quotation."
                    rows={(summary?.recentInvoices ?? []).slice(0, 6).map((i) => ({
                      id: `i-${i.id}`,
                      title: i.invoice_number || "—",
                      subtitle: i.customer_name || "—",
                      meta: i.invoice_date
                        ? formatDate(i.invoice_date)
                        : i.created_at
                          ? formatDate(i.created_at)
                          : "—",
                      amount: `${CURRENCY} ${formatNumber(i.total_amount)}`,
                      tone: "violet",
                    }))}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card className="shadow-card overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg">Quick Insights</CardTitle>
              <CardDescription>A snapshot of this month.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <InsightRow
                label="Receipts this month"
                value={loading ? null : String(summary?.receiptsCount ?? 0)}
                hint="Payments captured"
                icon={ReceiptIcon}
                tone="from-emerald-500 to-teal-400"
              />
              <InsightRow
                label="Invoices this month"
                value={loading ? null : String(summary?.invoicesCount ?? 0)}
                hint="Sales invoices issued"
                icon={ScrollText}
                tone="from-amber-500 to-orange-400"
              />
              <InsightRow
                label="Average invoice"
                value={
                  loading
                    ? null
                    : summary && summary.invoicesCount > 0
                      ? `${CURRENCY} ${formatNumber(
                          summary.totalRevenue / summary.invoicesCount,
                        )}`
                      : "—"
                }
                hint="Revenue ÷ invoices"
                icon={TrendingUp}
                tone="from-violet-500 to-fuchsia-400"
              />

            </CardContent>
          </Card>
        </section>
      )}

      {visibleShortcuts.length > 0 && (
        <section>
          <div className="flex items-end justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-foreground">
                Shortcuts
              </h2>
              <p className="text-sm text-muted-foreground">
                Jump straight into any module you have access to.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {visibleShortcuts.map((s) => (
              <Link
                key={s.key}
                to={s.to}
                className="group rounded-xl border bg-card p-4 shadow-card transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-10 w-10 rounded-lg bg-gradient-to-br ${s.tone} flex items-center justify-center text-white shadow-card shrink-0`}
                  >
                    <s.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">
                      {s.label}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {s.description}
                    </div>
                  </div>
                  <ArrowUpRight className="ml-auto h-4 w-4 text-muted-foreground transition group-hover:text-primary group-hover:translate-x-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

interface ActivityRow {
  id: string;
  title: string;
  subtitle: string;
  meta: string;
  amount: string;
  tone: "emerald" | "violet";
  badgeLabel?: string;
  badgeClass?: string;
}

function ActivityList({
  loading,
  rows,
  emptyMessage,
}: {
  loading: boolean;
  rows: ActivityRow[];
  emptyMessage: string;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="h-9 w-9 rounded-lg bg-muted animate-pulse shrink-0" />
              <div className="space-y-2 flex-1">
                <Shimmer className="h-3 w-32" />
                <Shimmer className="h-3 w-20" />
              </div>
            </div>
            <Shimmer className="h-4 w-20" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/20 p-8 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <ul className="divide-y rounded-lg border">
      {rows.map((row) => {
        const dot =
          row.tone === "emerald"
            ? "from-emerald-500 to-teal-400"
            : "from-violet-500 to-fuchsia-400";
        return (
          <li
            key={row.id}
            className="flex items-center justify-between gap-3 p-3 transition hover:bg-muted/40"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`h-9 w-9 rounded-lg bg-gradient-to-br ${dot} text-white flex items-center justify-center shrink-0`}
              >
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="font-mono text-sm text-foreground truncate">
                  {row.title}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {row.subtitle} · {row.meta}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {row.badgeLabel ? (
                <Badge
                  variant="outline"
                  className={`capitalize hidden sm:inline-flex ${row.badgeClass ?? ""}`}
                >
                  {row.badgeLabel}
                </Badge>
              ) : null}
              <div className="font-mono text-sm font-semibold text-foreground">
                {row.amount}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

interface ChartPalette {
  axis: string;
  grid: string;
  tooltipBg: string;
  tooltipText: string;
  tooltipBorder: string;
}

function useChartPalette(): ChartPalette {
  const { theme } = useTheme();
  if (theme === "dark") {
    return {
      axis: "#98a1b3",
      grid: "#283044",
      tooltipBg: "#161928",
      tooltipText: "#f0f3f7",
      tooltipBorder: "#283044",
    };
  }
  return {
    axis: "#6b7280",
    grid: "#e4e7eb",
    tooltipBg: "#ffffff",
    tooltipText: "#0f172a",
    tooltipBorder: "#e4e7eb",
  };
}

function formatMonthShort(ym: string): string {
  const parts = ym.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString(undefined, { month: "short" });
}

function formatMonthLong(ym: string): string {
  const parts = ym.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function truncateLabel(s: string, n = 32): string {
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}\u2026`;
}

interface ChartTooltipEntry {
  value?: number | string;
  payload?: unknown;
  name?: string;
}

interface ChartTooltipInjectedProps {
  active?: boolean;
  payload?: ChartTooltipEntry[];
}

function ChartTooltip({
  active,
  payload,
  palette,
  resolveLabel,
}: ChartTooltipInjectedProps & {
  palette: ChartPalette;
  resolveLabel: (entry: ChartTooltipEntry) => string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const first = payload[0];
  const label = resolveLabel(first);
  const value = Number(first?.value ?? 0);
  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        background: palette.tooltipBg,
        color: palette.tooltipText,
        borderColor: palette.tooltipBorder,
      }}
    >
      <div className="font-medium">{label}</div>
      <div className="font-mono text-xs opacity-80">
        {CURRENCY} {formatNumber(value)}
      </div>
    </div>
  );
}

function ChartSkeleton({ orientation }: { orientation: "vertical" | "horizontal" }) {
  if (orientation === "horizontal") {
    return (
      <div className="flex h-[320px] w-full flex-col justify-center gap-3 px-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-3 w-28 rounded bg-muted animate-pulse" />
            <div
              className="h-4 rounded bg-muted animate-pulse"
              style={{ width: `${30 + ((i * 11) % 60)}%` }}
            />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="flex h-[320px] w-full items-end justify-between gap-2 px-4 pb-6 pt-6">
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className="flex-1 rounded bg-muted animate-pulse"
          style={{ height: `${30 + ((i * 13) % 60)}%` }}
        />
      ))}
    </div>
  );
}

function ChartEmpty({ message }: { message: string }) {
  return (
    <div className="flex h-[320px] w-full items-center justify-center">
      <div className="max-w-sm rounded-lg border border-dashed bg-muted/20 px-6 py-8 text-center text-sm text-muted-foreground">
        {message}
      </div>
    </div>
  );
}

interface SalesPerformanceCardProps {
  loading: boolean;
  data: SalesPerformanceResponse | null;
  error: string | null;
}

function SalesPerformanceCard({ loading, data, error }: SalesPerformanceCardProps) {
  const palette = useChartPalette();

  const monthly = data?.monthly ?? [];
  const products = data?.products ?? [];
  const salespeople = data?.salespeople ?? [];

  const monthlyData = useMemo(
    () =>
      monthly.map((m) => ({
        month: m.month,
        label: formatMonthShort(m.month),
        longLabel: formatMonthLong(m.month),
        total: Number(m.total ?? 0),
      })),
    [monthly],
  );

  const productData = useMemo(
    () =>
      [...products]
        .sort((a, b) => b.total - a.total)
        .map((p) => ({
          fullName: p.name || "Unnamed product",
          label: truncateLabel(p.name || "Unnamed product"),
          total: Number(p.total ?? 0),
        })),
    [products],
  );

  const salespeopleData = useMemo(
    () =>
      [...salespeople]
        .sort((a, b) => b.total - a.total)
        .map((u) => ({
          fullName: u.name || "Unknown",
          label: truncateLabel(u.name || "Unknown"),
          total: Number(u.total ?? 0),
        })),
    [salespeople],
  );

  const monthlyHasData = monthlyData.some((m) => m.total > 0);
  const productsHasData = productData.length > 0;
  const salespeopleHasData = salespeopleData.length > 0;

  return (
    <Card className="shadow-card overflow-hidden">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-400 text-white flex items-center justify-center shadow-card shrink-0">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <CardTitle className="text-lg">Sales Performance</CardTitle>
            <CardDescription>
              Last 12 months · Sales invoices (excluding cancelled)
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {error ? (
          <div className="rounded-lg border border-dashed bg-muted/20 p-8 text-center text-sm text-muted-foreground">
            {error}
          </div>
        ) : (
          <Tabs defaultValue="monthly" className="w-full">
            <TabsList className="grid grid-cols-3 w-full max-w-md">
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="products">Top Products</TabsTrigger>
              <TabsTrigger value="salespeople">Salespeople</TabsTrigger>
            </TabsList>

            <TabsContent value="monthly" className="mt-4">
              {loading ? (
                <ChartSkeleton orientation="vertical" />
              ) : !monthlyHasData ? (
                <ChartEmpty message="No invoices yet — your first sale will appear here." />
              ) : (
                <div className="h-[320px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyData}
                      margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={palette.grid}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: palette.axis, fontSize: 12 }}
                        axisLine={{ stroke: palette.grid }}
                        tickLine={{ stroke: palette.grid }}
                      />
                      <YAxis
                        tick={{ fill: palette.axis, fontSize: 12 }}
                        axisLine={{ stroke: palette.grid }}
                        tickLine={{ stroke: palette.grid }}
                        tickFormatter={(value: number) => formatNumber(value)}
                        width={80}
                      />
                      <Tooltip
                        cursor={{ fill: palette.grid, opacity: 0.3 }}
                        content={
                          <ChartTooltip
                            palette={palette}
                            resolveLabel={(entry) => {
                              const payload = entry.payload as
                                | { longLabel?: string; label?: string }
                                | undefined;
                              return (
                                payload?.longLabel ??
                                payload?.label ??
                                ""
                              );
                            }}
                          />
                        }
                      />
                      <Bar
                        dataKey="total"
                        fill="#8b5cf6"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={48}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </TabsContent>

            <TabsContent value="products" className="mt-4">
              {loading ? (
                <ChartSkeleton orientation="horizontal" />
              ) : !productsHasData ? (
                <ChartEmpty message="No invoiced line items yet — once you bill a product it will show up here." />
              ) : (
                <div
                  className="w-full"
                  style={{
                    height: Math.max(280, productData.length * 36 + 48),
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={productData}
                      layout="vertical"
                      margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={palette.grid}
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        tick={{ fill: palette.axis, fontSize: 12 }}
                        axisLine={{ stroke: palette.grid }}
                        tickLine={{ stroke: palette.grid }}
                        tickFormatter={(value: number) => formatNumber(value)}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={200}
                        tick={{ fill: palette.axis, fontSize: 12 }}
                        axisLine={{ stroke: palette.grid }}
                        tickLine={{ stroke: palette.grid }}
                      />
                      <Tooltip
                        cursor={{ fill: palette.grid, opacity: 0.3 }}
                        content={
                          <ChartTooltip
                            palette={palette}
                            resolveLabel={(entry) => {
                              const payload = entry.payload as
                                | { fullName?: string; label?: string }
                                | undefined;
                              return (
                                payload?.fullName ?? payload?.label ?? ""
                              );
                            }}
                          />
                        }
                      />
                      <Bar
                        dataKey="total"
                        fill="#10b981"
                        radius={[0, 6, 6, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </TabsContent>

            <TabsContent value="salespeople" className="mt-4">
              {loading ? (
                <ChartSkeleton orientation="horizontal" />
              ) : !salespeopleHasData ? (
                <ChartEmpty message="No salesperson activity yet — invoices created by your team will show here." />
              ) : (
                <div
                  className="w-full"
                  style={{
                    height: Math.max(280, salespeopleData.length * 36 + 48),
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={salespeopleData}
                      layout="vertical"
                      margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={palette.grid}
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        tick={{ fill: palette.axis, fontSize: 12 }}
                        axisLine={{ stroke: palette.grid }}
                        tickLine={{ stroke: palette.grid }}
                        tickFormatter={(value: number) => formatNumber(value)}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={180}
                        tick={{ fill: palette.axis, fontSize: 12 }}
                        axisLine={{ stroke: palette.grid }}
                        tickLine={{ stroke: palette.grid }}
                      />
                      <Tooltip
                        cursor={{ fill: palette.grid, opacity: 0.3 }}
                        content={
                          <ChartTooltip
                            palette={palette}
                            resolveLabel={(entry) => {
                              const payload = entry.payload as
                                | { fullName?: string; label?: string }
                                | undefined;
                              return (
                                payload?.fullName ?? payload?.label ?? ""
                              );
                            }}
                          />
                        }
                      />
                      <Bar
                        dataKey="total"
                        fill="#f59e0b"
                        radius={[0, 6, 6, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}

interface InsightRowProps {
  label: string;
  value: string | null;
  hint: string;
  icon: IconType;
  tone: string;
}

function InsightRow({ label, value, hint, icon: Icon, tone }: InsightRowProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-muted/20 p-3">
      <div
        className={`h-10 w-10 rounded-lg bg-gradient-to-br ${tone} text-white flex items-center justify-center shrink-0`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        {value === null ? (
          <Shimmer className="mt-1 h-5 w-24" />
        ) : (
          <div className="text-base font-semibold text-foreground truncate">
            {value}
          </div>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground text-right max-w-[40%]">
        {hint}
      </div>
    </div>
  );
}
